// Revisión de contratos con IA (spec 012). Los tipos copian los view models de PractitionerContracts.API 019
// (contracts-api.md §0 y §3.6) con sus mismos nombres de campo; los enumerados viajan como texto.

export type ContractReviewScope = 'Differences' | 'BestPractices' | 'Risks' | 'ConceptReview';
export const ALL_REVIEW_SCOPES: ContractReviewScope[] = ['Differences', 'BestPractices', 'Risks', 'ConceptReview'];

export type ContractReviewStatus =
  | 'Queued'
  | 'Running'
  | 'Completed'
  | 'Failed'
  | 'Applied'
  | 'PartiallyApplied'
  | 'Reverted' // spec 015 (Contracts 021): sin ningún hallazgo aplicado después de deshacer
  | 'Unknown';
export const ACTIVE_REVIEW_STATUSES: ContractReviewStatus[] = ['Queued', 'Running'];
export const DECIDABLE_REVIEW_STATUSES: ContractReviewStatus[] = ['Completed', 'Applied', 'PartiallyApplied', 'Reverted'];
// Contracts 021 §4.2: se puede deshacer mientras quede algún hallazgo aplicado
export const UNDOABLE_REVIEW_STATUSES: ContractReviewStatus[] = ['Completed', 'Applied', 'PartiallyApplied', 'Reverted'];

export type ContractReviewTrigger = 'Manual' | 'AfterImport';

// D-7 (cross-project §5)
export type ContractReviewMode = 'Review' | 'NewAddendum';

// D-6: estado de parametrización, leído de GET contracts/{id}/parametrization-status (DEC-003)
export type ContractParametrizationState = 'unknown' | 'empty' | 'parameterized';

// Lista cerrada de IA 004 (ia-api.md §3; DEC-009). Tolerante a códigos nuevos.
export const FINDING_CATEGORIES = [
  'NewAddendum',
  'MissingConcept',
  'ExtraConcept',
  'PriceMismatch',
  'CurrencyMismatch',
  'OverheadMismatch',
  'PaymentScheduleMismatch',
  'StageMismatch',
  'BranchCycleMismatch',
  'ConditionMismatch',
  'ValidityMismatch',
  'PaymentTerms',
  'SettlementSetting',
  'BestPractice',
  'Risk'
] as const;
export type FindingCategory = (typeof FINDING_CATEGORIES)[number] | string;

// Lista cerrada de IA 004 (ia-api.md §4.1), en este orden (DEC-009). Tolerante a códigos nuevos.
export const CONCEPT_CHECK_CODES = [
  // A. Contra el documento
  'ConceptInDocument',
  'PriceMatchesDocument',
  'CurrencyMatchesDocument',
  'StagesMatchDocument',
  'PaymentScheduleMatchesDocument',
  'OverheadMatchesDocument',
  'ConditionsMatchDocument',
  'ValidityMatchesDocument',
  // B. Consistencia interna
  'ValuationTypeComplete',
  'ValuationTypeConsistent',
  'PercentageBaseValid',
  'ZeroPriceJustified',
  'CurrencyMatchesContract',
  'SettlementConfigConsistent',
  'InitialPeriodDateValid',
  'PaymentScheduleComplete',
  'ScheduleCoefficientsConsistent',
  'OverheadConsistent',
  'ConceptLinksValid',
  'NoOverlappingValidity',
  'EnabledAndApproved',
  'NoDuplicate',
  'ConditionsConsistent'
] as const;
export type ConceptCheckCode = (typeof CONCEPT_CHECK_CODES)[number] | string;

export type FindingSeverity = 'High' | 'Medium' | 'Low' | 'Info';
export const FINDING_SEVERITIES: FindingSeverity[] = ['High', 'Medium', 'Low', 'Info'];

export type FindingDecision = 'Pending' | 'Accepted' | 'Rejected';
export const FINDING_DECISIONS: FindingDecision[] = ['Pending', 'Accepted', 'Rejected'];

export type FindingApplyStatus = 'Applied' | 'Conflict' | 'Skipped' | 'Failed' | 'Undone';
export const FINDING_APPLY_STATUSES: FindingApplyStatus[] = ['Applied', 'Conflict', 'Skipped', 'Failed', 'Undone'];

// Contracts 021 §3.2: decidedBy de una decisión automática
export const SYSTEM_DECIDER = 'IA';
// Contracts 021 §3.1 (DEC-026): instrucciones para la IA
export const MAX_REVIEW_INSTRUCTIONS = 4000;

export type FindingOrigin = 'Rules' | 'Model' | 'Rules+Model';

export type ReviewEntityType =
  | 'Contract'
  | 'Addendum'
  | 'Stage'
  | 'Branch'
  | 'Cycle'
  | 'Concept'
  | 'PaymentSchedule'
  | 'ConceptLink';
export type ReviewPatchOp = 'Update' | 'Create' | 'Delete';
export type ReviewPatchTarget = 'Copy' | 'Base'; // patch.appliesTo (D-7)

export type ConceptVerdict = 'Ok' | 'Observations' | 'Incorrect' | 'NotInDocument';
export const CONCEPT_VERDICTS: ConceptVerdict[] = ['Ok', 'Observations', 'Incorrect', 'NotInDocument'];
export type ConceptCheckStatus = 'Ok' | 'Warning' | 'Error' | 'NotApplicable';

export interface ReviewEvidence {
  page: number | null;
  fileName: string | null;
  quote: string;
}

export interface ReviewTarget {
  entityType: ReviewEntityType;
  entityId: number | null;
  label: string;
}

export interface ReviewPatch {
  op: ReviewPatchOp;
  entityType: ReviewEntityType;
  entityId: number | null;
  fields: Record<string, unknown>;
  appliesTo: ReviewPatchTarget | null;
}

export interface ContractReviewFinding {
  id: number;
  reviewId: string;
  ordinal: number;
  key: string;
  scope: ContractReviewScope;
  category: FindingCategory;
  severity: FindingSeverity;
  title: string;
  description: string | null;
  evidence: ReviewEvidence[];
  target: ReviewTarget | null;
  currentValue: string | null;
  suggestedValue: string | null;
  patch: ReviewPatch | null;
  patchValid: boolean;
  patchInvalidReason: string | null;
  baseline: Record<string, unknown> | null;
  confidence: number | null;
  origin: FindingOrigin | null;
  decision: FindingDecision;
  decisionComment: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  overrideFields: Record<string, unknown> | null;
  applyStatus: FindingApplyStatus | null;
  applyMessage: string | null;
  appliedBy: string | null;
  appliedAt: string | null;
  currentValues: Record<string, unknown> | null;
  previousValues: Record<string, unknown> | null;
  appliedValues: Record<string, unknown> | null;
  createdEntityId: number | null;
  // Contracts 021 §3.2
  autoDecided?: boolean;
  undoneAt?: string | null;
  undoneBy?: string | null;
  undoMessage?: string | null;
  undoneValues?: Record<string, unknown> | null;
  patchRef?: string | null;
}

export interface ConceptVerdictCheck {
  check: ConceptCheckCode;
  status: ConceptCheckStatus;
  detail: string | null;
  origin: FindingOrigin | null;
}

export interface ConceptVerdictItem {
  contractConceptId: number | null;
  label: string;
  verdict: ConceptVerdict;
  checks: ConceptVerdictCheck[];
  findingKeys: string[];
  findingIds: number[];
}

export interface ExtractionSummary {
  source?: string;
  stages: number;
  concepts: number;
  branches: number;
  cycles: number;
  currency: string | null;
}

export interface ContractReview {
  id: string;
  contractId: number;
  mode: ContractReviewMode;
  addendumId: number | null;
  baseAddendumId: number | null;
  createdAddendumId: number | null;
  baseAddendumIsBlocked: boolean | null;
  status: ContractReviewStatus;
  trigger: ContractReviewTrigger;
  importProcessId: string | null;
  stage: string | null;
  progressPercent: number;
  model: string | null;
  scopes: ContractReviewScope[];
  fileIds: string[];
  errorMessage: string | null;
  createdBy: string;
  created: string;
  startTimestamp: string | null;
  endTimestamp: string | null;
  appliedBy: string | null;
  appliedAt: string | null;
  findingCount: number;
  pendingCount: number;
  acceptedCount: number;
  rejectedCount: number;
  appliedCount: number;
  conflictCount: number;
  skippedCount: number;
  failedCount: number;
  // Contracts 021 §3.2
  autoApply?: boolean;
  undoneCount?: number;
  // Sólo en el detalle
  instructions?: string | null;
  extractionSummary?: ExtractionSummary | null;
  warnings?: string[];
  findings?: ContractReviewFinding[];
  conceptVerdicts?: ConceptVerdictItem[];
}

export interface NewContractReview {
  mode: ContractReviewMode;
  addendumId?: number | null;
  baseAddendumId?: number | null;
  fileIds: string[];
  scopes: ContractReviewScope[];
  // Contracts 021 §3.1: null usa la configuración del servidor (aplica sola)
  autoApply?: boolean | null;
  instructions?: string | null;
}

// Campos del patch del hallazgo NewAddendum (op Create, entityType Addendum, appliesTo Copy)
export interface NewAddendumFields {
  ContractAddendumName: string;
  ContractAddendumDescription?: string | null;
  ContractAddendumExpiryStartDate: string;
  ContractAddendumExpiryEndDate?: string | null;
  ContractAddendumType: 0;
}

export interface UpdateFindingDecision {
  decision: FindingDecision;
  comment?: string | null;
}

// PATCH contracts/reviews/{ReviewId}/findings (DEC-004): 1..200 por pedido, todo o nada
export interface FindingDecisionItem extends UpdateFindingDecision {
  findingId: number;
}
export const MAX_BULK_DECISIONS = 200;
export const MAX_DECISION_COMMENT = 2000;

export interface ContractReviewApplyItem {
  findingId: number;
  key: string;
  applyStatus: FindingApplyStatus;
  message?: string | null;
  currentValues?: Record<string, unknown> | null;
  createdEntityId?: number | null;
}

export interface ContractReviewApplyResult {
  reviewId: string;
  status: ContractReviewStatus;
  createdAddendumId?: number | null;
  results: ContractReviewApplyItem[];
  appliedCount: number;
  conflictCount: number;
  skippedCount: number;
  failedCount: number;
  undoneCount?: number;
}

// GET contracts/{ContractId}/parametrization-status (DEC-003; Contracts 021 §2)
export interface ContractParametrizationStatus {
  contractId: number;
  isParameterized: boolean;
  hasSettlements?: boolean | null;
  canImport?: boolean;
  importBlockedReason?: string | null;
}
